import * as React from 'react';
import {useEffect, useState} from 'react';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {Button, Switch, theme} from 'antd';
import {FormShell} from 'components/form/FormShell';
import {RangeInput} from 'components/form/RangeInput';
import {OptionType, Select} from 'components/form/Select';
import {TextArea} from 'components/form/TextArea';
import {TextInput} from 'components/form/TextInput';
import {STATUS_OPTIONS} from 'helpers/constants';
import {usePrompt} from 'helpers/useBlocker';
import {checkIsAllDay} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {browserHistory} from 'routing/browserHistory';
import {appState} from 'store/AppStore';
import {TaskState} from 'store/tasks/TaskState';
import {ServiceTaskCreateDto} from '../../../../../../PRMGApiClient';

export const ServiceTaskEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {token} = theme.useToken();
  const isEdit = browserHistory.location.pathname.includes('edit');
  const [state] = useState(() => new TaskState());
  const [isAllDay, setIsAllDay] = useState(false);
  const employees = appState.employeesOptions;
  const {taskId, serviceId} = useParams();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: {errors, isDirty},
  } = useForm<ServiceTaskCreateDto>({
    mode: 'onTouched',
    defaultValues: {
      startDateTime: new Date(),
      endDateTime: new Date(),
    },
  });
  const startDate = watch('startDateTime');
  const endDate = watch('endDateTime');

  useEffect(() => {
    taskId && state.loadTask(Number(taskId));
  }, [state, taskId]);

  useEffect(() => {
    if (taskId) reset(state.item);
  }, [state.item, taskId, reset]);

  useEffect(() => {
    setIsAllDay(checkIsAllDay(startDate, endDate));
  }, [startDate, endDate]);

  usePrompt(t('Are you sure? There are unsaved changes.'), taskId ? isDirty : false);

  return (
    <>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => browserHistory.back()}
        style={{color: token.colorTextDescription}}
        className="px-0"
      >
        {t('Back')}
      </Button>
      <div className="px-48 max-w-[92rem] mx-auto">
        <FormShell
          isDirty={isDirty}
          onReset={() => reset()}
          onSubmit={handleSubmit((payload) => {
            console.log(serviceId, taskId);
            (serviceId || taskId) && state.saveTask(payload, Number(serviceId));
          })}
          onDelete={taskId ? () => state.deleteTask() : undefined}
          isLoading={state.isLoading}
        >
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2">
            <TextInput
              containerClassName="col-start-1"
              label={t('Task name')}
              error={errors.name}
              required
              {...register('name', {required: true})}
            />
            <div className="mb-3 col-start-1">
              <Switch className="mr-2" checked={isAllDay ?? false} onChange={(value) => setIsAllDay(value)} />
              <span>{t('All day')}</span>
            </div>
            <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2 col-span-2">
              <Controller
                name="startDateTime"
                control={control}
                rules={{required: true}}
                render={() => (
                  <RangeInput
                    id="startDateTime"
                    label={t('Start and end date')}
                    value={[startDate, endDate]}
                    required
                    isAllDay={isAllDay}
                    showTime
                    error={errors.startDateTime ?? errors.endDateTime}
                    onChange={(startDate, endDate) => {
                      setValue('startDateTime', startDate);
                      setValue('endDateTime', endDate);
                    }}
                  />
                )}
              />
            </div>
            <Controller
              name="assignee"
              control={control}
              rules={{required: true}}
              render={({field: {onChange, value}}) => (
                <Select
                  id="assignee"
                  className="col-start-1"
                  label={t('Assigned to')}
                  options={employees}
                  value={employees.find((item) => item.value === value?.id) ?? null}
                  error={errors.assignee as FieldError}
                  required
                  onChange={(option) =>
                    onChange(
                      appState.employees.find((employee) => {
                        return employee.id === (option as OptionType).value;
                      })
                    )
                  }
                />
              )}
            />
            {!isEdit && (
              <Controller
                name="status"
                control={control}
                rules={{required: true}}
                render={({field: {onChange, value}}) => (
                  <Select
                    id="state"
                    className="col-start-1"
                    label={t('State')}
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((item) => item.value === value) ?? null}
                    required
                    error={errors.status}
                    onChange={(option) => onChange((option as OptionType).value)}
                  />
                )}
              />
            )}
            <div className="col-start-1">
              <Controller
                name="description"
                control={control}
                render={({field: {onChange, value}}) => (
                  <TextArea
                    label={t('Description')}
                    id={'description'}
                    value={value ?? ''}
                    placeholder={t('Description')}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
        </FormShell>
      </div>
    </>
  );
});
